import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, MapPin, Trash2, Edit, Route } from 'lucide-react';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function RouteZonesPage() {
  const { token } = useAuth();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [zoneName, setZoneName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [notes, setNotes] = useState('');
  const [routeName, setRouteName] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxStops, setMaxStops] = useState('');

  useEffect(() => {
    loadZones();
  }, []);

  const loadZones = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/dashboard/route-zones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(res.data);
    } catch (error) {
      console.error('Error loading route zones:', error);
      toast.error('Failed to load route zones');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setZoneName('');
    setDayOfWeek('');
    setNotes('');
    setRouteName('');
    setServiceArea('');
    setStartTime('');
    setEndTime('');
    setMaxStops('');
  };

  const buildPayload = () => ({
    zoneName: zoneName.trim(),
    dayOfWeek: parseInt(dayOfWeek),
    routeName: routeName.trim() || null,
    serviceArea: serviceArea.trim() || null,
    startTime: startTime || null,
    endTime: endTime || null,
    maxStops: maxStops ? parseInt(maxStops, 10) : null,
    notes: notes.trim() || null,
  });

  const handleAddZone = async () => {
    if (!zoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }
    if (dayOfWeek === '') {
      toast.error('Please select a day of the week');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/api/dashboard/route-zones`, buildPayload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Route day added');
      setShowAddDialog(false);
      resetForm();
      loadZones();
    } catch (error) {
      console.error('Error adding route zone:', error);
      toast.error(error.response?.data?.detail || 'Failed to add route day');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditZone = async () => {
    if (!zoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }
    if (dayOfWeek === '') {
      toast.error('Please select a day of the week');
      return;
    }

    try {
      setSubmitting(true);
      await axios.patch(
        `${API_URL}/api/dashboard/route-zones/${editingZone.id}`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Route day updated');
      setShowEditDialog(false);
      setEditingZone(null);
      resetForm();
      loadZones();
    } catch (error) {
      console.error('Error updating route zone:', error);
      toast.error(error.response?.data?.detail || 'Failed to update route day');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Are you sure you want to delete this route zone?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/dashboard/route-zones/${zoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route zone deleted');
      loadZones();
    } catch (error) {
      console.error('Error deleting route zone:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete route zone');
    }
  };

  const openEditDialog = (zone) => {
    setEditingZone(zone);
    setZoneName(zone.zoneName);
    setDayOfWeek(zone.dayOfWeek.toString());
    setNotes(zone.notes || '');
    setRouteName(zone.routeName || '');
    setServiceArea(zone.serviceArea || '');
    setStartTime(zone.startTime || '');
    setEndTime(zone.endTime || '');
    setMaxStops(zone.maxStops != null ? String(zone.maxStops) : '');
    setShowEditDialog(true);
  };

  const getDayName = (dayNum) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayNum);
    return day ? day.label : 'Unknown';
  };

  // Group zones by day of week
  const zonesByDay = DAYS_OF_WEEK.map(day => ({
    ...day,
    zones: zones.filter(z => z.dayOfWeek === day.value)
  })).filter(day => day.zones.length > 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-1">
              Route Zones
              <PageHelp
                testId="route-zones"
                title="Route Zones"
                purpose="Group jobs by area and pick which day you serve each area — less driving."
                actions={[
                  { label: 'Add Route Zone', desc: 'name an area and assign it a day of the week' },
                ]}
                nextStep="Add a zone for each town or area you cover."
              />
            </h2>
            <p className="text-muted-foreground">
              Define geographic zones and assign specific days for each area
            </p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-zone-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Route Zone
          </Button>
        </div>

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Route className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold text-primary">How Route Zones Work</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  When clients book from a specific town or area, they'll only see available appointments 
                  on your designated route day for that zone. This helps you group appointments geographically 
                  and reduces travel time between jobs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Route Zones List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : zones.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No Route Zones Yet</h3>
              <p className="text-muted-foreground mt-1 mb-4">
                Add route zones to organize your appointments by geographic area.
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Zone
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {zonesByDay.map(({ value, label, zones: dayZones }) => (
              <Card key={value}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                      {label.charAt(0)}
                    </span>
                    {label}
                  </CardTitle>
                  <CardDescription>
                    {dayZones.length} zone{dayZones.length !== 1 ? 's' : ''} scheduled
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3">
                    {dayZones.map((zone) => (
                      <div 
                        key={zone.id} 
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        data-testid={`zone-${zone.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">
                              {zone.routeName || zone.zoneName}
                              {zone.routeName && zone.routeName !== zone.zoneName && (
                                <span className="text-xs text-muted-foreground ml-2">({zone.zoneName})</span>
                              )}
                            </p>
                            {zone.serviceArea && (
                              <p className="text-xs text-muted-foreground">{zone.serviceArea}</p>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                              {(zone.startTime || zone.endTime) && (
                                <span>
                                  ⏱ {zone.startTime || '—'} – {zone.endTime || '—'}
                                </span>
                              )}
                              {zone.maxStops && <span>Max {zone.maxStops} stops</span>}
                            </div>
                            {zone.notes && (
                              <p className="text-sm text-muted-foreground mt-1">{zone.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(zone)}
                            data-testid={`edit-zone-${zone.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteZone(zone.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-zone-${zone.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Zone Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Route Day</DialogTitle>
              <DialogDescription>
                Define a geographic zone and assign it to a specific day of the week.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="zoneName">Zone Name *</Label>
                <Input
                  id="zoneName"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="e.g., Klamath Falls, Bonanza"
                  data-testid="zone-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dayOfWeek">Route Day *</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger data-testid="day-select">
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value.toString()}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="routeName">Route Name (optional)</Label>
                <Input
                  id="routeName"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="e.g., North Loop, Bonanza Run"
                  data-testid="route-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serviceArea">Service Area (optional)</Label>
                <Input
                  id="serviceArea"
                  value={serviceArea}
                  onChange={(e) => setServiceArea(e.target.value)}
                  placeholder="e.g., Hwy 39 corridor, between Mile 10–25"
                  data-testid="service-area-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="startTime">Start</Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    data-testid="start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">End</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    data-testid="end-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxStops">Max stops</Label>
                  <Input
                    id="maxStops"
                    type="number"
                    min={1}
                    max={500}
                    value={maxStops}
                    onChange={(e) => setMaxStops(e.target.value)}
                    placeholder="—"
                    data-testid="max-stops-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes about this route day..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleAddZone} disabled={submitting} data-testid="save-zone-btn">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Route Day'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Zone Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Route Day</DialogTitle>
              <DialogDescription>
                Update the name, area, hours, or notes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editZoneName">Zone Name *</Label>
                <Input
                  id="editZoneName"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="e.g., Klamath Falls, Bonanza"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDayOfWeek">Route Day *</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value.toString()}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editRouteName">Route Name (optional)</Label>
                <Input
                  id="editRouteName"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="e.g., North Loop"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editServiceArea">Service Area (optional)</Label>
                <Input
                  id="editServiceArea"
                  value={serviceArea}
                  onChange={(e) => setServiceArea(e.target.value)}
                  placeholder="e.g., Hwy 39 corridor"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="editStartTime">Start</Label>
                  <Input id="editStartTime" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editEndTime">End</Label>
                  <Input id="editEndTime" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editMaxStops">Max stops</Label>
                  <Input
                    id="editMaxStops"
                    type="number"
                    min={1}
                    max={500}
                    value={maxStops}
                    onChange={(e) => setMaxStops(e.target.value)}
                    placeholder="—"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editNotes">Notes (optional)</Label>
                <Textarea
                  id="editNotes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes about this route day..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingZone(null); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleEditZone} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
